import { IconPlayTvguide } from '@assets/icon-play-tvguide';
import type { TvChannel, TvChannelShowtime } from '@lib/graphql/generated';
import { useTvGuideStore } from '@src/stores/tvguide';
import { createDate, isCurrent } from '@utils/dateHelpers';
import { getShowtimeWatchUrl } from '@utils/showtimeWatchUrl';
import { format } from 'date-fns';
import { memo } from 'react';
import { twMerge } from 'tailwind-merge';

export interface TimelineProgramEntryProps {
  entry: TvChannelShowtime;
  channel: TvChannel;
  currentTime: Date;
  left: number;
  width: number;
}

export const TimelineProgramEntry = memo(({ entry, channel, currentTime, left, width }: TimelineProgramEntryProps) => {
  const setOpenEntryData = useTvGuideStore((state) => state.setOpenEntryData);
  const isCurrentEntry = isCurrent(entry.start, entry.stop, currentTime);
  const watchUrl = getShowtimeWatchUrl(entry, currentTime, channel);

  return (
    <button
      type="button"
      className="group absolute h-full flex-none px-0.5 content-auto [contain-intrinsic-height:72px] hover:z-10 hover:content-visible focus:outline-none md:[contain-intrinsic-height:112px]"
      style={{
        left: `${left}px`,
        width: `${width}px`,
        containIntrinsicWidth: `${width}px`,
      }}
      onClick={() => {
        setOpenEntryData(entry, channel);
      }}
    >
      <div
        className={twMerge(
          'pointer-events-none -left-10 grid h-full justify-start overflow-clip rounded border border-gray-650/25 bg-gradient-to-t from-gray-840 to-gray-700 transition-colors duration-300 group-hover:z-20 group-hover:w-max group-hover:min-w-max group-hover:border-primary',
          isCurrentEntry && 'from-gray-600 to-gray-600',
        )}
        style={{
          width: `${width - 4}px`,
        }}
      >
        <div className="sticky left-0 grid h-full grid-rows-[1fr_auto] items-stretch text-clip px-3 py-2">
          <div className="relative block h-full text-clip text-left font-bold text-gray-400 text-sm tracking-2px">
            <div
              className={twMerge(
                'absolute top-0 left-0 line-clamp-1 min-w-max group-hover:relative group-hover:min-w-72',
                isCurrentEntry && 'text-primary',
              )}
            >
              {watchUrl && (
                <span className="mr-2 inline-flex align-middle" role="img" aria-label="Auf Joyn anschauen">
                  <IconPlayTvguide classProps={{ root: 'text-white' }} />
                </span>
              )}
              {format(createDate(entry.start), 'HH:mm')}&nbsp;-&nbsp;{format(createDate(entry.stop), 'HH:mm')}
            </div>
          </div>
          <div className="relative block h-full text-clip text-left text-base lg:text-xl">
            <div className="absolute bottom-0 left-0 line-clamp-1 min-w-max group-hover:relative group-hover:min-w-72">
              {entry.title}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
});

TimelineProgramEntry.displayName = 'TimelineProgramEntry';
